import { BaseConstruction } from './BaseConstruction';
import { ConstructionStep } from '../types';
import { Point } from '../objects/Point';
import { Segment } from '../objects/Segment';
import { Circle } from '../objects/Circle';
import { Arc } from '../objects/Arc';
import { Angle } from '../objects/Angle';
import { DrawPoint, DrawLine, DrawCircle, DrawArc } from '../animations/DrawPoint';
import { MoveCompass, Highlight, Fade } from '../animations/MoveCompass';
import { SVGRenderer } from '../core/SVGRenderer';

// Tangent from External Point to Circle
export class TangentExternalPointConstruction extends BaseConstruction {
  public buildSteps(options: { x?: number; y?: number; radius?: number; distance?: number } = {}): ConstructionStep[] {
    const center = { x: options.x ?? 320, y: options.y ?? 380 };
    const r = options.radius ?? 120;
    const dist = options.distance ?? 260;

    const pP = { x: center.x + dist, y: center.y }; // External point P
    const pMid = { x: center.x + dist / 2, y: center.y };

    // Tangent points formula
    const alpha = Math.acos(r / dist);
    const pT1 = SVGRenderer.polarToCartesian(center, r, -alpha);
    const pT2 = SVGRenderer.polarToCartesian(center, r, alpha);

    const pointO = new Point(this.renderer, center.x, center.y, 'O');
    const pointP = new Point(this.renderer, pP.x, pP.y, 'P');
    const pointT1 = new Point(this.renderer, pT1.x, pT1.y, 'T1');
    const pointT2 = new Point(this.renderer, pT2.x, pT2.y, 'T2');

    const mainCircle = new Circle(this.renderer, center, r, false);
    const segOP = new Segment(this.renderer, center, pP, true);

    const tangent1 = new Segment(this.renderer, pP, pT1, false);
    const tangent2 = new Segment(this.renderer, pP, pT2, false);

    const rad1 = new Segment(this.renderer, center, pT1, true);
    const rad2 = new Segment(this.renderer, center, pT2, true);

    [pointO, pointP, pointT1, pointT2, mainCircle, segOP, tangent1, tangent2, rad1, rad2].forEach((o) => o.setVisible(false));

    return [
      {
        id: 'step1',
        title: '1. Given Circle O and External Point P',
        description: 'Draw circle centered at O and external point P.',
        narration: 'Step 1: Given circle O and external point P.',
        duration: 1500,
        animations: [new DrawPoint(pointO), new MoveCompass(this.compass, center, r), new DrawCircle(mainCircle, this.compass), new DrawPoint(pointP)],
      },
      {
        id: 'step2',
        title: '2. Connect OP and Bisect Segment',
        description: 'Connect O to P and bisect segment OP.',
        narration: 'Step 2: Connecting OP and constructing bisecting arc.',
        duration: 1500,
        animations: [new DrawLine(segOP, this.ruler)],
      },
      {
        id: 'step3',
        title: '3. Intersect Arcs to locate Tangent Points T1 and T2',
        description: 'Draw arcs from midpoint of OP to locate contact points T1 and T2 on circle.',
        narration: 'Step 3: Marking tangent contact points T1 and T2 on circle perimeter.',
        duration: 1800,
        animations: [new DrawPoint(pointT1), new DrawPoint(pointT2), new DrawLine(rad1), new DrawLine(rad2)],
      },
      {
        id: 'step4',
        title: '4. Draw Tangent Lines PT1 and PT2',
        description: 'Use ruler to draw tangent lines from P through T1 and T2.',
        narration: 'Construction complete! Pair of tangents PT1 and PT2 drawn from external point P.',
        duration: 1800,
        animations: [
          new Fade(this.compass, false),
          new DrawLine(tangent1, this.ruler),
          new DrawLine(tangent2, this.ruler),
          new Highlight(tangent1),
          new Highlight(tangent2),
        ],
      },
    ];
  }
}

// Concentric Circles
export class ConcentricCirclesConstruction extends BaseConstruction {
  public buildSteps(options: { x?: number; y?: number; r1?: number; r2?: number; r3?: number } = {}): ConstructionStep[] {
    const center = { x: options.x ?? 400, y: options.y ?? 350 };
    const r1 = options.r1 ?? 80;
    const r2 = options.r2 ?? 140;
    const r3 = options.r3 ?? 200;

    const pointO = new Point(this.renderer, center.x, center.y, 'O');
    const c1 = new Circle(this.renderer, center, r1, false);
    const c2 = new Circle(this.renderer, center, r2, false);
    const c3 = new Circle(this.renderer, center, r3, false);

    [pointO, c1, c2, c3].forEach((o) => o.setVisible(false));

    return [
      {
        id: 'step1',
        title: '1. Mark Common Center O',
        description: 'Locate common center O.',
        narration: 'Step 1: Locating common center O.',
        duration: 1000,
        animations: [new DrawPoint(pointO)],
      },
      {
        id: 'step2',
        title: '2. Draw Inner, Middle, and Outer Concentric Circles',
        description: 'Draw concentric circles of radii r1, r2, r3.',
        narration: 'Step 2: Sweeping concentric circles around common center O.',
        duration: 2500,
        animations: [
          new MoveCompass(this.compass, center, r1),
          new DrawCircle(c1, this.compass),
          new MoveCompass(this.compass, center, r2),
          new DrawCircle(c2, this.compass),
          new MoveCompass(this.compass, center, r3),
          new DrawCircle(c3, this.compass),
        ],
      },
    ];
  }
}
